// TypeScript-Typen für Master-REST-Responses.
// Subsets der Master-DTOs in packages/management-service/src/api/dto/*.
// Wir holen nur was M1-UI braucht; bei Schema-Drift Run-Time-Fehler statt Build-Fehler.

export interface User {
  id: string;
  email: string;
  role: "admin" | "user";
  supervisorId: string | null;
  defaultNotificationBindingId: string | null;
  // #214/#215 (B5-T2): Gitea-Login-Name des Owners (null = keine Gitea-Anbindung).
  giteaUsername: string | null;
  // #223: Monats-Budget-Limit des Users (null = Plattform-Default greift). Optional =
  // Version-Skew-Hardening (alter Master ohne das Feld).
  budgetPolicy?: { monthlyLimitUsd: number | null };
  createdAt: string;
}

export interface AgentBrainSpec {
  imageId: string;
}

export interface AgentAssignment {
  nodeId: string;
}

// #214/#215 (UC-B1/B2) Konzept B: gebundenes Gitea-Workspace-Repo (null bis Bind).
export interface AgentWorkspaceRepo {
  repoFullName: string;
  cloneUrl: string;
  branch: string;
  deployedCommit: string | null;
  // #215 (E2/E3): Fork-Quelle (für Flash-Vorbelegung). null = aus altem Stand / kein Seed.
  seedRepo: string | null;
  seedBranch: string | null;
}

export type DesiredRunState = "running" | "stopped" | "suspended";

export type LifecyclePhase =
  | "unassigned"
  | "stopped"
  | "starting"
  | "running"
  | "degraded"
  | "draining"
  | "crashed"
  | "crashloopbackoff"
  | "suspended"
  | "migrating"
  | "unknown";

export interface Agent {
  id: string;
  name: string;
  description: string;
  type: "user-agent" | "service-agent";
  ownerId: string | null;
  status: "offline" | "available" | "busy";
  // #NEU-A/#201: Lifecycle (docs/09). desiredRunState = Admin-Absicht,
  // lifecyclePhase = beobachteter FSM-Zustand. status bleibt Liveness-Tick.
  desiredRunState: DesiredRunState;
  lifecyclePhase: LifecyclePhase;
  cwd: string;
  brainSpec: AgentBrainSpec | null;
  assignment: AgentAssignment | null;
  // #214/#215 Konzept B: Gitea-Workspace-Repo (Brain-Content). null = keine Gitea-Anbindung.
  workspaceRepo: AgentWorkspaceRepo | null;
  revokedAt: string | null;
  capabilities: string | null;
  createdAt: string;
  lastSeen: string | null;
  // #226: Discovery-Steuerung (Flags + Scope; volle CV via /discovery/profile).
  discovery: {
    scope: string;
    discoverable: boolean;
    allowRegister: boolean;
    allowConnectionRequest: boolean;
  };
  // #224R: Modell-Governance. fixedModel = Admin-Fix (read-only Spiegel von
  // config.managed.model; GESETZT wird er über die Config-API, nicht hier).
  // allowedModels = Owner-Whitelist (leer ⇒ ganzer Katalog). Die wählbaren Modelle
  // liefert GET /models (dynamischer supportedModels()-Katalog).
  modelPolicy: {
    fixedModel: string | null;
    allowedModels: string[];
    // #P001 (UC-3): Kontext-Defaults (chat/case). OPTIONAL = Version-Skew — ein älterer
    // Master liefert das Feld nicht, die Sektion darf daran nicht scheitern.
    defaults?: { chat: string | null; case: string | null };
  };
  // #223: Monats-Budget-Limit (null = Plattform-Default greift). Optional = Version-Skew-
  // Hardening (ein alter Master ohne das Feld darf die UI nicht crashen).
  budgetPolicy?: { monthlyLimitUsd: number | null };
  // #223 UC-A: Per-Turn-Budget-Cap (null = Plattform-Default, 0 = unbegrenzt, >0 = Cap).
  // Optional = Version-Skew-Hardening (alter Master ohne das Feld).
  turnBudget?: { limitUsd: number | null };
  // #229: Workspace-Live-Sync-Kurzsicht (Device-ID + ob aktiv). null = Feature aus
  // (kein Relay konfiguriert). Voller Status via GET /agents/:id/workspace/sync.
  syncthing?: { deviceId: string; enabled: boolean } | null;
  // #245 (Teil B): opt-in — Agent wird aktiv geweckt, wenn eine ausgehende
  // Verbindungsanfrage akzeptiert wird. Optional = Version-Skew-Hardening.
  connectionWakeOnAccepted?: boolean;
  // #244: Outbound-Relay-Policy (final/verbose). Beide false = heutiges Verhalten.
  // Optional = Version-Skew-Hardening.
  outboundRelay?: { final: boolean; verbose: boolean };
  // #335: darf dieser Agent den Puls (die Startseite) seines Nutzers bauen? Default an.
  // Optional = Version-Skew-Hardening (älterer Master ohne das Feld).
  pulsEnabled?: boolean;
}

// #335 (UC-5): die ADMIN-Sicht auf den Puls — ausschliesslich Zahlen.
//
// ⚠ Es gibt bewusst KEINEN Weg zum Inhalt (Konzept Entscheidung 12): der Puls enthält
// Mail-Inhalte und Entwürfe des Nutzers. `version: null` = es gibt (noch) kein Dokument.
export interface PulsMeta {
  enabled: boolean;
  version: number | null;
  updatedAt: string | null;
  source: "scheduled" | "manual" | "chat" | null;
  bytes: number | null;
}

// #229: voller Workspace-Sync-Status (GET /agents/:id/workspace/sync).
export interface WorkspaceSyncStatus {
  available: boolean;
  relayConfigured: boolean;
  deviceId: string | null;
  folderId: string;
  relayUrl: string;
  folderType: "sendreceive" | "receiveonly" | "sendonly";
  userDevices: { deviceId: string; label: string }[];
  ignorePatterns: string[];
  online: boolean;
  connectedDeviceIds: string[];
}

// #223: Budget-Report-Status (GET /agents/:id/budget bzw. /users/:id/budget, admin-only).
export interface AgentBudgetStatus {
  yearMonth: string;
  agentId: string;
  agentMtdUsd: number;
  agentLimitUsd: number;
  userMtdUsd: number | null;
  userLimitUsd: number | null;
  blocked: boolean;
  history: { yearMonth: string; spentUsd: number; turnCount: number }[];
}

export interface UserBudgetStatus {
  yearMonth: string;
  userId: string;
  userMtdUsd: number;
  userLimitUsd: number;
  blocked: boolean;
  perAgent: { agentId: string; name: string; spentUsd: number; limitUsd: number }[];
}

// #224R: ein Modell aus dem dynamischen Katalog (GET /models, von den Brains via
// SDK supportedModels() gemeldet). value = Identifier (für /model + Push), die
// anderen Felder sind UX-Strings.
export interface ModelInfo {
  value: string;
  displayName: string;
  description: string;
}

// #P002 (UC-1): das feste Vokabular, mit dem ein Modell-Eintrag beschrieben wird. Zwilling der
// Registry `MODEL_META_KEYS` im Master (`domain/value-objects/ModelMeta.ts`) — eine Erweiterung
// dort will hier nachgezogen werden. Es ist eine ADMIN-AUSSAGE, kein technischer Nachweis.
export interface ModelMeta {
  /** Darf dieser Eintrag firmen-heikle Daten verarbeiten? */
  sensitiveDataOk: boolean;
  /** Läuft das Modell lokal oder in der Cloud? */
  hosting: "local" | "cloud";
}

// #P001 (UC-2): die Ziel-Anzeige eines wählbaren Modell-Eintrags — was steht hinter dem
// Namen? Nie Ziel-Interna (Basis-URL, Schlüssel): der Endpoint ist owner-lesbar.
export interface ModelTargetView {
  name: string;
  kind: "anthropic" | "slot";
  target?: { model: string; provider: string; contextWindowTokens: number | null };
  enabled: boolean;
  // #P002 (UC-1): was der Eintrag darf. `?` ist Version-Skew — ein älterer Master liefert das
  // Feld nicht, und dann behauptet die Beschriftung lieber NICHTS, als etwas zu erfinden.
  meta?: ModelMeta;
}

// #P001 (UC-2): Antwort von GET /agents/:id/models. `targets`/`fixedTarget` sind OPTIONAL —
// Version-Skew: ein älterer Master liefert nur die Namen, dann zeigt der Picker eben nur sie.
export interface AgentModelsView {
  selectable: string[];
  fixedModel: string | null;
  targets?: ModelTargetView[];
  fixedTarget?: ModelTargetView | null;
  // #P001 (UC-3): das UNIVERSUM des Agenten (ohne Whitelist-Filter) + die gesetzten
  // Kontext-Defaults — Quelle der beiden Default-Picker. Optional = Version-Skew.
  universe?: ModelTargetView[];
  defaults?: { chat: string | null; case: string | null };
}

// #227: Anthropic-Auth-Status eines scopes (Plattform-Default oder per-Agent).
// Das Secret ist write-only → kommt NIE zurück (nur isSet + method + updatedAt).
export interface AnthropicAuthInfo {
  scope: string;
  isSet: boolean;
  method: string | null;
  updatedAt: string | null;
}

// #243: LLM-Provider-Routing (optionales LiteLLM-Gateway). Secret-frei (kein credential/gatewayKey).
export interface ProviderRoutingInfo {
  scope: string;
  // #P001 (UC-1): KEIN Routing-weites `enabled` mehr — der Schalter sitzt pro Slot im Mapping.
  provider: string;
  baseUrl: string | null;
  // #243: wohin gehört das Credential beim Ziel-Provider? null = Provider-Standard.
  // Der Header-NAME ist nicht geheim — nur der Wert bleibt write-only.
  authHeader: string | null;
  authScheme: string | null;
  // #307: Ein Slot trägt sein eigenes ZIEL — Modell plus optional eigener Anbieter, eigene
  // Basis-URL, eigene Zugangsdaten und eigene Auth-Position. Nur so kann Opus zum Original und
  // Sonnet zum günstigen Fremdanbieter gehen. Die Felder oben (provider/baseUrl/…) sind die
  // Vorgabe für Slots, die nichts Eigenes tragen.
  mapping: Record<string, SlotTargetView>;
  hasCredential: boolean;
  configured: boolean;
}

// #307: Slot-Ziel, wie der Server es zeigt — mit `hasCredential` STATT des Werts (write-only).
export interface SlotTargetView {
  model: string;
  // #P001 (UC-1): An/Aus DIESES Slots. Nur aktive Slots sind wählbar und stehen am Gateway
  // in der Allowlist; inaktive bleiben als Konfiguration erhalten.
  enabled: boolean;
  // #P001: Slot-NAME für Claude Codes Nebenaufgaben (`ANTHROPIC_SMALL_FAST_MODEL`).
  // null = der Slot selbst. Muss ein anderer AKTIVER Slot desselben Routings sein.
  smallModel: string | null;
  provider: string | null;
  baseUrl: string | null;
  hasCredential: boolean;
  authHeader: string | null;
  authScheme: string | null;
  contextWindowTokens: number | null;
  // #318: Request-Parameter, die der Gateway an DIESEM Ziel verwirft (LiteLLM
  // `additional_drop_params`). Nicht geheim (nur Namen) → der Server zeigt sie offen, und genau
  // deshalb kann die Oberfläche sie beim Speichern unverändert zurückschicken. `?` ist
  // Version-Skew: ein älterer Master liefert das Feld nicht.
  litellmDropParams?: string[];
  // #P002 (UC-1): was dieser Slot darf — vom Admin gesetzt. Nicht geheim, steht offen in der
  // Antwort und wird beim Speichern unverändert zurückgeschickt (Muster litellmDropParams).
  // `?` = Version-Skew; fehlt es, gilt der geschlossene Stand „Cloud, nicht heikel".
  meta?: ModelMeta;
}

// #307: was die Oberfläche beim Speichern schickt. Kurzform (nur Modellname) für Slots ohne
// eigenes Ziel — damit bleiben Bestandsdaten unverändert lesbar.
export type SlotTargetInput =
  | string
  | {
      model: string;
      provider?: string | null;
      baseUrl?: string | null;
      credential?: string;
      authHeader?: string | null;
      authScheme?: string | null;
      contextWindowTokens?: number | null;
      // #P001 (UC-1): fehlt ⇒ aus (default-closed). Die Kurzform (blosser Modellname) ist damit
      // immer ein AUSgeschalteter Slot — wer einschalten will, schickt die lange Form.
      enabled?: boolean;
      // #P001: Slot-Name fürs kleine Modell; null/weglassen = der Slot selbst.
      smallModel?: string | null;
      // #318: weglassen = keine Zusatzparameter. Das Feld ist NICHT write-only wie `credential` —
      // es wird gelesen und zurückgeschickt; ein leeres Feld heißt hier also „keine", nicht
      // „unverändert". Der Master baut die Deployments bei jedem Speichern neu: was hier fehlt,
      // ist danach auch am Gateway weg.
      litellmDropParams?: string[];
      // #P002 (UC-1): Teil-Objekt erlaubt (fehlender Schlüssel = Default). Die Oberfläche
      // schickt es vollständig, sobald es vom Default abweicht — sonst bleibt die Kurzform.
      meta?: Partial<ModelMeta>;
    };

// #226: vollständiges Discovery-Profil (inkl. CV) aus GET /agents/:id/discovery/profile.
export interface DiscoveryProfile {
  agentId: string;
  cv: string | null;
  capabilities: string | null;
  scope: string;
  discoverable: boolean;
  allowRegister: boolean;
  allowConnectionRequest: boolean;
}

// #185: ChannelInventorySnapshot — Plugin-Container published live-Identität.
export interface ChannelInventorySnapshot {
  updatedAt: string;
  externalIdentity: string | null;
  pluginStatus: string;
  pluginType?: string;
  pluginVersion?: string;
  raw?: Record<string, unknown>;
}

// #185: Channel.type ist freier String (Plugin-Modell).
export interface Channel {
  id: string;
  type: string;
  name: string;
  status: "enrolled" | "online" | "offline" | "disabled";
  config: Record<string, unknown>;
  // #298: Namen der geheimen config-Schluessel — die WERTE liefert der Master nie.
  // Optional = Version-Skew (aelterer Master kennt das Feld nicht).
  secretConfigKeys?: string[];
  inventorySnapshot: ChannelInventorySnapshot | null;
  // Channel-Setup-Flow: grober Setup-Zustand (orthogonal zum status). Optional = Version-Skew.
  setupState?: ChannelSetupStateValue | null;
  lastHeartbeatAt: string | null;
  createdAt: string;
}

// Channel-Setup-Flow (SDUI): grober State + opaques Plugin-Schema aus GET /channels/:id/setup.
export type ChannelSetupStateValue = "needs_setup" | "in_progress" | "ready" | "attention";
export interface ChannelSetup {
  setupState: ChannelSetupStateValue | null;
  schema: unknown | null;
}

// #185: ChannelNatsCredentials — kommt einmalig in der Create-Channel-Response.
export interface ChannelNatsCredentials {
  natsUrl: string;
  natsJwt: string;
  natsSeed: string;
  natsUserPubKey: string;
  natsUserJwtId: string;
  expiresAt: string;
}

export interface NodeCapacity {
  cpu: number;
  memoryGb: number;
  maxAgents: number;
}

export interface Node {
  id: string;
  hostname: string;
  status: "online" | "offline";
  backends: string[];
  capacity: NodeCapacity;
  runningAgents: number;
  createdAt: string;
  lastSeen: string;
}

export interface AgentConnection {
  id: string;
  agentAId: string;
  agentBId: string;
  status: "active" | "revoked";
  createdByUserId: string;
  createdAt: string;
  revokedAt: string | null;
  revokedByUserId: string | null;
}

export interface AgentConnectionRequest {
  id: string;
  requesterAgentId: string;
  targetAgentId: string;
  reason: string | null;
  status: "pending" | "accepted" | "rejected";
  createdByUserId: string;
  createdAt: string;
  decidedAt: string | null;
  decidedByUserId: string | null;
}

export interface AgentConfig {
  managed: Record<string, unknown>;
  owner: Record<string, unknown>;
  mcpServers: Record<string, unknown>;
  version: number;
}

export interface Session {
  id: string;
  userId: string | null;
  agentId: string;
  // #301 (UC-2): chat | case — Standard-Listen liefern nur chat; case sieht
  // nur der Admin (?kind=case|all), die Prozess-Sicht kommt mit #303.
  kind: "chat" | "case";
  title: string;
  status: "active" | "idle" | "archived" | string;
  totalCostUsd: number;
  turnCount: number;
  // #224 (UC-0): `model` = konfiguriert. `lastObservedModel` = zuletzt vom Brain
  // gemeldetes (Telemetrie, kann durch interne Hilfsmodelle abweichen).
  model: string;
  lastObservedModel: string | null;
  cwd: string;
  createdAt: string;
  lastUsedAt: string;
}

export interface BrainImage {
  id: string;
  name: string;
  description: string;
  imageRef: string;
  digest: string | null;
  active: boolean;
  createdBy: string;
  createdAt: string;
}

// #253 (UC-A): Auth eines Connectors als discriminated union (kind = none|static|oauth|login).
// Secret-frei — der Wert (Token/Passwort) liegt im Vault.
export type ConnectorAuth =
  | { kind: "none" }
  | { kind: "static"; header: string; scheme: string }
  | {
      kind: "oauth";
      clientId: string | null;
      scopes: string[];
      authServerHint: string | null;
      // #336 (UC-2): explizite Endpoints = Discovery-Bypass. Beide oder keiner (Server erzwingt
      // das). Optional getypt, damit ein aelterer Master (ohne die Felder) kompiliert.
      authorizationEndpoint?: string | null;
      tokenEndpoint?: string | null;
    }
  // #336 (UC-1): OAuth-2-Client-Credentials (Service-Identitaet). Secret-frei — clientId und
  // clientSecret kommen beim Verbinden (write-only → Vault).
  | { kind: "client-credentials"; tokenUrl: string; scopes: string[]; authStyle: "basic" | "body" }
  | {
      kind: "login";
      tokenUrl: string;
      bodyFormat: "json" | "form";
      userField: string;
      passField: string;
      tokenPath: string;
      injectHeader: string;
      injectScheme: string;
      ttlSeconds: number | null;
    };

// #231: Connector-Katalog (Remote-MCP). KEINE Secrets — clientSecret/Tokens liegen im
// Master-Vault. `connection` (optional) = secret-freier Verbindungs-Status (list/detail).
// #297: WIE verbindet man einen Connector? Kommt FERTIG vom Master — die UI leitet
// nichts mehr aus transport/auth ab (genau das ist frueher gedriftet). Unbekanntes
// `method` = "nicht verfuegbar" rendern, nie raten.
export interface ConnectSpecField {
  name: string;
  secret: boolean;
  required: boolean;
  label: string | null;
  placeholder: string | null;
}
export type ConnectorConnectSpec =
  | { method: "none" }
  | { method: "api-key" }
  | { method: "login" }
  | { method: "oauth" }
  // #336 (UC-1): zwei Secret-Felder (clientId + clientSecret), kein Browser-Flow.
  | { method: "client-credentials" }
  | { method: "env"; fields: ConnectSpecField[] }
  | { method: "unavailable"; reason: string };

// Die env-Felder einer Spec — leer, wenn es kein Formular ist. EINE Zeile statt einer
// zweiten Ableitung; bewusst hier, damit kein Aufrufer wieder eigene Regeln erfindet.
export function envFelder(spec: ConnectorConnectSpec | undefined): ConnectSpecField[] {
  return spec?.method === "env" ? spec.fields : [];
}

// Die Verfahren, die diese UI kennt. Ein Master kann neue erfinden — dann darf die
// Oberflaeche NICHT raten (und schon gar nicht "kein Credential noetig" behaupten,
// wie es der else-Zweig frueher tat). Default-closed: unbekannt = nicht bedienbar.
const BEKANNTE_METHODEN = ["none", "api-key", "login", "oauth", "client-credentials", "env", "unavailable"] as const;

// Eine fehlende Spec (aelterer Master) und ein unbekanntes Verfahren enden beide hier:
// als bewusst-nicht-bedienbar, mit unterscheidbarem Grund fuer den Text.
export function spec2(spec: ConnectorConnectSpec | undefined): ConnectorConnectSpec {
  if (!spec) return { method: "unavailable", reason: "kein-spec" };
  if (!BEKANNTE_METHODEN.includes(spec.method as (typeof BEKANNTE_METHODEN)[number])) {
    return { method: "unavailable", reason: "unbekanntes-verfahren" };
  }
  return spec;
}

// EIN Text pro Grund — sonst behauptet der Dialog etwas anderes als die Detailseite
// (Review-Fund #297-LOW-6: er nannte hart "Datei-Credential", egal welcher Grund).
export function unavailableText(reason: string): string {
  if (reason === "file-credential") {
    return "Datei-Credential — die Datei-Auslieferung ist noch nicht gebaut. Ein Eintrag hier würde nichts bewirken.";
  }
  if (reason === "kein-spec") {
    return "Dieser Master liefert noch keine Verbindungs-Beschreibung — bitte aktualisieren.";
  }
  if (reason === "unbekanntes-verfahren") {
    return "Dieses Verbindungs-Verfahren kennt diese Oberfläche noch nicht (neuerer Master).";
  }
  return "Dieser Connector lässt sich hier nicht verbinden.";
}

export interface Connector {
  id: string;
  // #297: vom Master beschrieben (secret-frei). OPTIONAL, damit der Compiler den
  // Version-Skew-Fall erzwingt (aeltere Antwort ohne das Feld) statt ihn wegzudefinieren.
  connectSpec?: ConnectorConnectSpec;
  // #273/#279: Fachverantwortlicher. null = gehoert dem System (alle Admins verwalten es).
  ownerId: string | null;
  name: string;
  description: string;
  type: string; // Phase 1: "remote-mcp"
  url: string;
  transport: string; // auto | streamable-http | sse | local | cli | openapi
  // #253 (UC-A): Auth als discriminated union (secret-frei).
  auth: ConnectorAuth;
  // unified-managed-mcp: nur bei transport=local (command/args/envSchema), sonst null.
  local: {
    command: string;
    args: string[];
    envSchema: { name: string; secret: boolean; required: boolean; label: string | null; placeholder: string | null }[];
  } | null;
  // #249: nur bei transport=cli (bin/installCmd/authShape/binding/extraEnv), sonst null.
  cli: {
    bin: string;
    installCmd: string | null;
    uninstallCmd: string | null;
    authShape: string;
    binding: string | null;
    extraEnv: { name: string; secret: boolean; required: boolean; label: string | null; placeholder: string | null }[];
  } | null;
  // #253: nur bei transport=openapi (baseUrl/specUrl/mode/includeTags), sonst null.
  openapi: {
    baseUrl: string;
    specUrl: string;
    mode: string;
    includeTags: string[];
  } | null;
  createdAt: string;
  updatedAt: string;
  connection?: { connected: boolean; expiresAt: string | null; oauthClientSecretSet?: boolean };
  // unified-managed-mcp: weiche Warnungen (Version nicht gepinnt) — nach create/patch.
  warnings?: string[];
}

// #237 (UC-status): Live-MCP-Server-Status eines Brains (secret-frei). source/managed wird
// client-seitig durch Abgleich gegen die enabled Connectors ermittelt (nicht vom Server).
export interface McpServerStatusView {
  name: string;
  status: "connected" | "failed" | "needs-auth" | "pending" | "disabled";
  scope?: string;
  error?: string;
  serverInfo?: { name: string; version: string };
  tools: string[];
}
export interface McpStatusResponse {
  brainOnline: boolean;
  servers: McpServerStatusView[];
}

// #235: Plattform-weite Policy-/Default-Knöpfe (DB-backed, admin-editierbar, live).
export interface PlatformSettings {
  strictMarketplaces: boolean;
  /** #275 (E-9): Vier-Augen bei Artefakt-Freigaben. Optional = Version-Skew. */
  artifactApprovalRequiresSecondPerson?: boolean;
  budgetDefaultAgentUsd: number;
  budgetDefaultUserUsd: number;
  budgetResetTz: string;
  turnBudgetDefaultUsd: number;
  sessionDefaultModel: string;
  // #P001 (UC-3): Default-Modell neuer Prozess-Läufe.
  caseDefaultModel: string;
  /**
   * #P002 (UC-1): was die Anthropic-Modelle dürfen — EIN Wert für alle Aliase (nicht pro Alias,
   * nicht pro Agent). `?` = Version-Skew; die Maske zeigt dann den geschlossenen Default.
   */
  anthropicModelMeta?: ModelMeta;
  sessionDefaultCwd: string;
  marketplaceFetchAllowInsecure: boolean;
  connectorOauthAllowInsecure: boolean;
  brainImageAllowedRegistries: string[];
  attachmentMaxBytes: number;
  attachmentMimeWhitelist: string[];
  scheduledTasksMaxPerAgent: number;
  connectionMaxPendingOutbound: number;
  // #279: Firmen-Policy + eigene Quota fuer Connector-Zugriffs-Anfragen.
  connectorAccessRequiresApproval: boolean;
  connectorAccessMaxPending: number;
  masterDefaultStopBehavior: "all" | "current";
  jwtTtlSeconds: number;
  natsJwtDefaultTtlSeconds: number;
  permissionTimeoutMs: number;
  caseDispatchTimeoutMinutes: number; // #301 (UC-3): Dispatch-Frist pro Prozess-Schritt
  caseStuckTimeoutMinutes: number; // #301 (UC-5c): Frist, nach der ein stehender Lauf blockiert wird
  settingsResolveDenylist: string[];
  // #238 Observability (OpenTelemetry)
  otelEnabled: boolean;
  otelEndpoint: string;
  otelProtocol: "grpc" | "http/protobuf";
  otelTracesEnabled: boolean;
  otelDisableAnthropicTelemetry: boolean;
  otelLogToolDetails: boolean;
  otelLogUserPrompts: boolean;
  otelLogToolContent: boolean;
  otelLogRawApiBodies: boolean;
  auditRetentionDays: number;
}

// #232: Plugin-Katalog (Claude-Code-Marketplace-Plugins). DB = Wahrheit; Settings generiert.
export type MarketplaceSource =
  | { type: "github"; repo: string; ref?: string | null }
  | { type: "url"; url: string }
  | { type: "git"; url: string; ref?: string | null }
  | { type: "local"; path: string };

export interface Marketplace {
  id: string;
  name: string;
  source: MarketplaceSource;
  scope: string; // platform | agent:<id>
  autoUpdate: boolean;
  // #276 (E-10): "own" = Repo auf unserem Gitea (Inhalt verwaltbar), "external" = fremde
  // Quelle (nur lesbar). Optional getypt, damit ein älterer Master die Seite nicht bricht.
  origin?: "own" | "external";
  /** #292 (UC-2): Verantwortlicher (owns-Kante) — NUR im Detail (GET /:id) enthalten.
   *  null = ownerlos (System, alle Admins); undefined = Liste/älterer Master. */
  ownerId?: string | null;
  createdAt: string;
  updatedAt: string;
}

// #287: Zustand eines Katalog-Eintrags.
// - available: im Manifest enthalten.
// - orphaned:  Manifest erfolgreich gelesen, Eintrag fehlt → verwaist (wird nicht mehr
//              ausgeliefert, die Zuweisung bleibt aber bestehen).
// - unknown:   Quelle war nicht lesbar → keine Aussage möglich (NICHT „entfernt"!).
export type PluginCatalogStatus = "available" | "orphaned" | "unknown";

// #287: Katalog-Eintrag — Metadaten kommen LIVE aus der marketplace.json, `ownerAvailable`
// ist unsere Entscheidung. Anker = (marketplaceId, name); es gibt keine Plugin-Id mehr.
export interface Plugin {
  marketplaceId: string;
  marketplaceName: string;
  name: string;
  ref: string;
  description: string;
  version: string | null;
  status: PluginCatalogStatus;
  // #288 (UC-1): Plattform-Freigabe — „diesem Plugin vertrauen wir". Ohne sie ist das
  // Plugin weder zuweisbar noch auslieferbar. Die ADMIN-Sicht zeigt es trotzdem, markiert;
  // die Owner-Sicht (User-App) bekommt es serverseitig gar nicht erst.
  approved: boolean;
  ownerAvailable: boolean;
}

// #287 (UC-3): Konnte die Quelle gelesen werden? Eine nicht lesbare Quelle ist ein
// LADEFEHLER — die UI darf sie NIE als leere Liste darstellen.
export interface CatalogSource {
  marketplaceId: string;
  marketplaceName: string;
  ok: boolean;
  error: string | null;
}

export interface PluginCatalog {
  plugins: Plugin[];
  sources: CatalogSource[];
}

// #290 (UC-3): Plugin-Detail — EIN Ort für ein Plugin. `repoId` ist die SERVER-Zusage,
// dass der Katalog auf ein managebares eigenes Repo verweist (sonst null); `agentName`
// null = Agent nicht (mehr) auflösbar (UI zeigt orphaned statt zu verschweigen).
export interface PluginUsage {
  agentId: string;
  agentName: string | null;
  enabled: boolean;
  scope: string;
}

export interface PluginDetail {
  plugin: Plugin;
  /** #292 (UC-1): ein Plugin existiert fachlich ab dem ANLEGEN — vor der Katalog-Freigabe
   *  ist es „in Aufnahme" (Meta aus dem Entwurfs-Manifest). Optional = Version-Skew-safe. */
  phase?: "live" | "in-aufnahme";
  /** nur bei in-Aufnahme: Koordinaten für Freigeben/Verwerfen (mp-Repo + Entwurfs-Branch). */
  draft?: { repoId: string; branch: string; state: "draft" | "submitted"; canApprove: boolean } | null;
  /** #292 (UC-2): GEERBT vom Marketplace (read-only am Plugin — eigene Kante erst #280). */
  ownerId?: string | null;
  /** #293 (UC-2): ausstehende Änderung/Entfernung an diesem live-Plugin — Banner mit
   *  Entscheidung direkt am Fachobjekt. Optional = Version-Skew. */
  pendingChange?: {
    art: "aenderung" | "entfernung";
    felder?: PluginFeldAenderung[];
    repoId: string;
    branch: string;
    state: "draft" | "submitted";
    canApprove: boolean;
  } | null;
  sources: CatalogSource[];
  /** Server-Zusage: eigenes plg-Repo ODER (bei in-marketplace) das Katalog-Repo. */
  repoId: string | null;
  /** nur bei in-marketplace: Ordner des Plugins IM Katalog-Repo (z.B. "plugins/x"). */
  repoPath?: string | null;
  usedBy: PluginUsage[];
}

// #232 (UC-2/3): Per-Agent-Plugin-Sicht (Katalog × Enablement + installiert-Drift).
export interface AgentPluginSummary {
  marketplaceId: string;
  marketplaceName: string;
  name: string;
  ref: string;
  description: string;
  version: string | null;
  status: PluginCatalogStatus;
  // #288 (UC-1): Plattform-Freigabe — „diesem Plugin vertrauen wir". Ohne sie ist das
  // Plugin weder zuweisbar noch auslieferbar. Die ADMIN-Sicht zeigt es trotzdem, markiert;
  // die Owner-Sicht (User-App) bekommt es serverseitig gar nicht erst.
  approved: boolean;
  ownerAvailable: boolean;
  enabled: boolean;
  scope: "managed" | "owner" | null;
  installed: boolean;
  /** #294 (UC-2): installierte Version (Inventory) + Update-Signal (≠ Katalog-Version).
   *  Optional = Version-Skew mit älterem Master. */
  installedVersion?: string | null;
  updateAvailable?: boolean;
}

export interface AgentPluginView {
  plugins: AgentPluginSummary[];
  sources: CatalogSource[];
}

// #231 (UC-2): Verbindungs-Status (POST /connect, GET /:id/connection — secret-frei).
export interface ConnectorConnectionStatus {
  connectorId: string;
  authType: string;
  connected: boolean;
  expiresAt: string | null;
  oauthClientSecretSet?: boolean; // confidential client: App-Secret hinterlegt?
}

// #231 (UC-3/4/6): Katalog-Connector + enabled-Flag für einen Agent (Per-Agent-Section).
// connected/connectedScope (UC-6): ob + auf welcher Scope-Stufe (agent/user/platform) für
// diesen Agent ein Credential verfügbar ist.
export interface AgentConnectorSummary {
  connectorId: string;
  // #297: vom Master beschrieben — ersetzt das mehrdeutige authType. OPTIONAL (Skew).
  connectSpec?: ConnectorConnectSpec;
  name: string;
  authType: string;
  transport: string;
  enabled: boolean;
  connected: boolean;
  connectedScope: "agent" | "user" | "platform" | "none" | null;
}

// #231 (UC-4): Reverse-Lookup — Agents, die einen Connector aktiviert haben.
export interface ConnectorAgentRef {
  agentId: string;
  enabled: boolean;
}

// #196: Settings-Profil — Config-Template (3 Layer), beim Agent-Create als
// Snapshot in die Agent-Config gemerged.
// #215 (E2): seedRepo/seedBranch = Gitea-Repo+Branch, aus dem der Workspace
// beim Agent-Create geforkt wird. null = kein Seed → Agent ohne Workspace-Repo.
// #281: Blaupausen-Achsen eines Profils — nur Referenzen (IDs), nie Credentials.
export interface ProfilePluginRef {
  marketplaceId: string;
  pluginName: string;
  scope: "managed" | "owner";
}
export interface ProfileDefaults {
  model?: string | null;
  monthlyBudgetUsd?: number | null;
  turnBudgetUsd?: number | null;
}
export interface ProfileBlueprint {
  brainImageId: string | null;
  plugins: ProfilePluginRef[];
  connectors: { connectorId: string }[];
  defaults: ProfileDefaults;
}

export interface SettingsProfile {
  id: string;
  name: string;
  description: string;
  config: {
    managed: Record<string, unknown>;
    owner: Record<string, unknown>;
    mcpServers: Record<string, unknown>;
  };
  seedRepo: string | null;
  seedBranch: string | null;
  // #281: optional deklariert → version-skew-sicher gegen einen älteren Master ohne das Feld.
  blueprint?: ProfileBlueprint;
  createdBy: string;
  createdAt: string;
}

// #215 (E2): kompakte Repo-Info aus GET /gitea/repos (Seed-Auswahl im Profil).
export interface GiteaRepoSummary {
  fullName: string;
  defaultBranch: string;
  empty: boolean;
}

// #182: ChannelBinding = User × Channel × externalId. verifiedAt entscheidet
// ob der Master von sich aus Notifications an diese externalId schickt.
// In Phase 1 setzt der Admin verifiedAt manuell; Phase 2 ersetzt das durch OTP.
export interface ChannelBinding {
  id: string;
  userId: string;
  channelId: string;
  externalId: string;       // canonical, vom Channel-Plugin normalisiert
  displayLabel: string | null;
  agentId: string | null;   // #182: expliziter Target. null = Fallback (1-Agent-Annahme)
  verifiedAt: string | null;
  createdAt: string;
}

export interface ExecResult {
  agentId: string;
  exitCode: number | null;
  stdout: string;
  stderr: string;
  durationMs: number;
  truncated: boolean;
  timedOut: boolean;
  runAs: string;
}

// #238 UC-3: Audit-of-record (Compliance-Trail mit Integritäts-Hash-Kette).
export interface AuditEvent {
  seq: number;
  ts: string;
  actor: string | null;
  action: string;
  target: string | null;
  details: Record<string, unknown>;
  prevHash: string;
  hash: string;
  // #239 B1 — OCSF-Felder (optional; bei Alt-Einträgen null).
  actorType?: string | null;
  targetType?: string | null;
  outcome?: "success" | "failure" | null;
  requestId?: string | null;
  traceId?: string | null;
  ip?: string | null;
}

export interface AuditListResponse {
  events: AuditEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditChainStatus {
  ok: boolean;
  count: number;
  brokenAtSeq?: number;
}

// #239 C1: aggregierte System-Health (alle Komponenten).
export type HealthStatus = "ok" | "degraded" | "down" | "unknown";
export interface ComponentHealth {
  status: HealthStatus;
  detail?: string;
}
export interface CountHealth extends ComponentHealth {
  total: number;
  healthy: number;
  breakdown?: Record<string, number>;
}
// #327: letzter Lauf des rollierenden Backups (deploy/backup.sh). `at`/`sizeBytes` sind
// null, wenn nichts Verlässliches bekannt ist.
export interface BackupHealth extends ComponentHealth {
  at: string | null;
  sizeBytes: number | null;
}
export interface SystemHealth {
  overall: HealthStatus;
  ts: string;
  components: {
    master: ComponentHealth;
    nats: ComponentHealth;
    gitea: ComponentHealth;
    nodes: CountHealth;
    agents: CountHealth;
    channels: CountHealth;
    // Optional: ein älterer Master liefert das Feld nicht (Version-Skew) — dann bleibt die
    // Zeile weg, statt „unbekannt" zu behaupten.
    backup?: BackupHealth;
  };
}

// #254: redigierter Lizenz-Status (GET /license). enabled=false → Subsystem nicht
// konfiguriert (state="DISABLED", übrige Felder fehlen).
export type LicenseState = "LICENSED" | "GRACE" | "DEGRADED" | "LOCKED" | "DISABLED";
export interface LicenseSnapshot {
  enabled: boolean;
  state: LicenseState;
  customer?: string | null;
  edition?: string | null;
  deploymentId?: string | null;
  features?: string[];
  limits?: { agents: number; users: number; nodes: number; channels: number } | null;
  leaseSeq?: number;
  leaseExpiresAt?: string | null;
  licenseExpiresAt?: string | null;
  graceEndsAt?: string | null;
  trial?: boolean;
  revoked?: boolean;
  lastRefreshAt?: string | null;
  lastRefreshError?: string | null;
}

// #270 (UC-1): Git-Content-Manager — managebare Repos (Marketplaces + Templates) +
// Content-Sicht (Tree/File) über den Master. Adressierung über server-vergebene IDs.
export interface ManagedRepo {
  id: string;
  kind: "marketplace" | "template" | "plugin" | "workspace" | "process"; // #276 Plugin-Repo · #283 workspace (owner-gescopt) · #282/#300 Prozess
  agentId?: string; // #283: nur bei kind=workspace (Server-Zusage)
  /** Nur bei kind="marketplace" — von der API zugesagt (nicht aus der Id raten). */
  marketplaceId?: string;
  displayName: string;
  repoFullName: string;
  branch: string; // der LIVE-Stand (= freigegeben)
  // #275 (E-4): freigabepflichtig oder direkt beschreibbar. Bei "direct" blendet die UI die
  // gesamte Lifecycle-Bedienung aus — sonst zeigte sie Knöpfe, die im Backend nicht greifen.
  lifecycle?: "governed" | "direct"; // optional = Version-Skew mit älterem Master
}

// #290 (UC-2): Fach-Sicht Workspace-Vorlage — Entity-Ebene über dem Repo. Bewusst KEIN
// Git-Vokabular (kein ref/branch/lifecycle): die Dateien zeigt der geteilte
// RepoContentPanel-Baustein, der über die ManagedRepo-Sicht läuft.
export interface WorkspaceTemplate {
  id: string; // opakes Fach-Handle — die UI leitet daraus NICHTS ab
  name: string;
  /** Server-Zusage: unter dieser ManagedRepo-ID läuft der Content-Baustein (Review-Fund:
   *  vorher aus dem opaken Handle abgeleitet — genau der E-10-Bruch). Optional wegen
   *  Version-Skew mit älterem Master. */
  repoId?: string;
  repoFullName: string; // nur Anzeige — Adressierung läuft über id/repoId
  usedByProfiles: Array<{ id: string; name: string }>;
  agentCount: number; // Agents, deren Workspace aus dieser Vorlage geforkt wurde
  /** #292 (UC-2): Verantwortlicher — NUR im Detail befüllt (Liste bewusst ohne). */
  ownerId?: string | null;
}

// #291 (UC-1): „in Aufnahme" — ein Plugin, das ein offener Entwurf dem Katalog hinzufügt.
// Fachlich existiert es schon (der Autor hat es angelegt), nur noch nicht live.
export interface PluginInAufnahme {
  name: string;
  description: string;
  version: string | null;
  draftBranch: string;
  draftState: "draft" | "submitted";
  canApprove: boolean;
}

// #293 (UC-1): ausstehende Änderung/Entfernung an einem BESTEHENDEN Katalog-Eintrag —
// die Entscheidung hängt am Fachobjekt (Plugin), nicht an einem Branch-Titel.
export interface PluginFeldAenderung {
  feld: "description" | "version" | "source";
  alt: string | null;
  neu: string | null; // source: beide null (nur Flag — der Wert ist Infrastruktur)
}

export interface PluginAenderung {
  name: string;
  art: "aenderung" | "entfernung";
  felder?: PluginFeldAenderung[];
  draftBranch: string;
  draftState: "draft" | "submitted";
  canApprove: boolean;
}

export interface MarketplacePending {
  manageable: boolean;
  liveReadable: boolean;
  repoId: string | null; // Server-Zusage fürs eingebettete Content-Panel
  drafts: RepoDraft[];
  pluginsInAufnahme: PluginInAufnahme[];
  /** #293: Änderungen an bestehenden Einträgen. Optional = Version-Skew (älterer Master). */
  pluginsGeaendert?: PluginAenderung[];
  /** Entwürfe mit unlesbarem Manifest (Gegenstück zu liveReadable) — wird benannt,
   *  statt sie still als „sonstige Entwürfe" zu zeigen. */
  draftsUnreadable: number;
}

// #291 (UC-3): die globale Freigabe-Arbeitsliste.
export interface PendingRepoDrafts {
  repoId: string;
  kind: "marketplace" | "template" | "plugin"; // Workspaces sind `direct` — nie in der Freigabe-Arbeitsliste
  marketplaceId?: string; // Server-Zusage für den Link in die Fach-Sicht
  displayName: string;
  drafts: RepoDraft[];
}

// #275/#278: Entwurf, Änderungs-Vorschau und Version. UI-Vokabular — Branch/PR/Tag
// tauchen in der Oberfläche NIE auf (BRIEF §2.2).
export interface RepoDraft {
  branch: string; // technisch "draft/<slug>"; nur als Adresse, nie als Anzeige
  slug: string;
  title: string;
  state: "draft" | "submitted";
  pullNumber: number | null;
  createdBy: string | null; // letzter Bearbeiter (Commit-Autor)
  canApprove: boolean; // vom Server entschieden (PDP) — die UI rät nicht
}

export interface DraftChanges {
  branch: string;
  pullNumber: number | null;
  files: Array<{ filename: string; status: string; additions: number; deletions: number }>;
  additions: number;
  deletions: number;
  schemaOk: boolean;
  schemaIssues: Array<{ path: string; message: string }>;
}

export interface RepoVersion {
  tag: string;
  commitSha: string;
  isCurrent: boolean;
}

export interface RepoTreeEntry {
  name: string;
  path: string;
  type: "file" | "dir";
  size: number;
}

export interface RepoFile {
  name: string;
  path: string;
  sha: string;
  size: number;
  isBinary: boolean;
  content: string | null;
}

// #274: Rollen-Achse. `kind` unterscheidet frei anlegbare Fach-Rollen von den drei
// kuratierten System-Rollen (unveränderlich, tragen additive Plattform-Capabilities).
export interface Role {
  id: string;
  name: string;
  kind: "fach" | "system";
  description: string;
  memberCount: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// #302 (Review M-4): die Rollen EINES Users. Bewusst die SCHLANKE Form des Servers
// (`SelfRoleResponse` — ohne memberCount/createdBy, Datensparsamkeit Cert-R6) und NICHT `Role`.
//
// ⚠ #331: der Geltungsbereich („gilt nur für Einrichtung Z") ist ERSATZLOS gestrichen — eine
// Rolle ist wieder eine globale Menge von Personen, sonst nichts. Eine Rolle steht damit
// höchstens EINMAL je Person. Ein älterer Master liefert `scope` noch mit; das Feld wird
// schlicht ignoriert (es steht bewusst nicht mehr im Typ, damit es niemand wieder liest).
export interface UserRoleEntry {
  id: string;
  name: string;
  kind: "fach" | "system";
  description: string;
}

export interface RoleMember {
  userId: string;
  email: string | null;
  assignedBy: string | null;
  assignedAt: string;
}

/**
 * #332 (Wire aus #331 UC-4): ein wählbarer Mensch aus dem Firmen-Adressbuch
 * (`GET /users/available?instanceId=&roleId=`).
 *
 * Nur INTERNE, aktive Nutzer — nie External Users, nie Agenten, nie deaktivierte. Der Master
 * ist der Wächter; die Oberfläche filtert NICHTS nach, sonst gäbe es zwei Regeln.
 * `mitgliedDerRolle` ist ohne `roleId` immer `false` (dann gibt es keine Rolle zu markieren).
 */
export interface AvailableUser {
  userId: string;
  anzeigeName: string;
  mitgliedDerRolle: boolean;
}

// #279: Zugriffs-Antrag auf einen Connector mit Fachverantwortlichem (oder — wenn die
// Firma es einschaltet — auch auf einen ownerlosen System-Connector).
export interface ConnectorAccessRequest {
  id: string;
  agentId: string;
  connectorId: string;
  requestedByUserId: string;
  reason: string | null;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  decidedAt: string | null;
  decidedByUserId: string | null;
  // #296: Anzeige-Namen (Version-Skew-safe optional)
  agentName?: string | null;
  connectorName?: string | null;
  requestedByEmail?: string | null;
}

// #282: Prozess-Registry (Template-Seite). Spiegel von ProcessResponse (Master-DTO).
export interface ProcessDefinitionView {
  id: string;
  name: string;
  description: string;
  repoName: string;
  managedRepoId?: string; // Server-Zusage; optional = Version-Skew-Haertung (Review L5)
  branch: string;
  ownerId: string;
  status: "draft" | "active" | "paused";
  instantiationRoles?: string[]; // #300: Denorm der Aktivierung (Server-Zusage; optional = Skew-Härtung)
  activation: {
    version: string;
    commitSha: string;
    activatedAt: string;
    activatedBy: string;
  } | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProcessReadinessGapView {
  code: string;
  severity: "block" | "warn";
  target: string;
  message: string;
}

export interface ProcessReadinessView {
  ready: boolean;
  gaps: ProcessReadinessGapView[];
}

// Nachtrag 2026-08-22: Versions-Sicht für den Freigeben-&-Aktivieren-Flow.
export interface ProcessVersionsView {
  activeVersion: string | null;
  specVersion: string | null;
  specVersionTagged: boolean;
  /** Version freigegeben, Live-Inhalt weicht aber vom Tag ab → Version erhöhen. */
  specDirty: boolean;
  tags: string[];
}

// #300: Instanz-Ebene. Die Listen-Sicht ist bewusst konfigurationsfrei (Mandantenschutz —
// inputDefaults/settingsOverrides sind Betriebsdaten des Inhabers).
export interface ProcessInstanceSummaryView {
  id: string;
  templateId: string;
  ownerId: string;
  agentId: string | null;
  triggerKind: string | null;
  status: "draft" | "ready" | "not_ready" | "paused";
  gapCount: number;
  blockCount: number;
  createdAt: string;
  updatedAt: string;
}

// #320: EIN Plugin-Bezug — dieselbe Form wie im Master (Anker `(marketplaceId, pluginName)`).
export interface ProcessPluginRefView {
  marketplaceId: string;
  pluginName: string;
}

export interface ProcessInstanceView {
  id: string;
  templateId: string;
  ownerId: string;
  agentId: string | null;
  /**
   * #P002 (UC-7): WER die Einrichtung ausführt (`spec.agent.fixed ?? agentId`).
   *
   * ⚠ OPTIONAL getippt (Version-Skew: ein älterer Master kennt das Feld nicht) — und die
   * Oberfläche zeigt heute bewusst NICHTS Neues: den gebundenen Agenten sieht sie schon.
   * Das Feld ist der Weg für Flächen, die eine Vorlage mit fixiertem Service-Agenten
   * darstellen wollen (dort ist `agentId` per Definition `null`).
   */
  effectiveAgentId?: string | null;
  trigger: { kind: string; config: Record<string, unknown> } | null;
  inputDefaults: Record<string, string | number | boolean>;
  settingsOverrides: Record<string, string | number | boolean>;
  model: string | null;
  /**
   * #320 (UC-2): Instanz-Override (`null` = erbt die Prozess-Vorgabe) + die aufgelöste
   * Liste. Alle drei Felder OPTIONAL — ein älterer Master kennt sie nicht, und dann darf
   * die Karte gar nicht erst erscheinen (Version-Skew).
   *
   * `effectivePluginsSource` sagt, woher die Liste kommt: `instance` (eigene Auswahl),
   * `spec` (Vorgabe des Prozesses) oder `unknown` (Vorgabe nicht abrufbar).
   *
   * ⚠ Drei Zustände von `effectivePlugins`, drei Bedeutungen: `undefined` = älterer Master
   * (Feld gibt es nicht) · `null` = nicht ermittelbar (immer mit `source: "unknown"`) ·
   * `[]` = wirklich keine. Ein `[]` für „nicht ermittelbar" wäre eine Behauptung.
   */
  plugins?: ProcessPluginRefView[] | null;
  effectivePlugins?: ProcessPluginRefView[] | null;
  effectivePluginsSource?: "instance" | "spec" | "unknown";
  /**
   * #330 (UC-1, Wire aus #329): die Freigabe-Schritte der AKTIVEN Version samt Erlaubnis-
   * Schaltern und Entscheider-Fakten.
   *
   * ⚠ DREI Zustände, drei Bedeutungen (Muster `effectivePlugins`, #320): `undefined` =
   * älterer Master ⇒ die Karte „Wer entscheidet" erscheint gar nicht · `null` = die
   * Definition ist nicht lesbar ⇒ die Karte erscheint und sagt genau das, aber es lässt sich
   * nichts wählen (auf einer unbekannten Schritt-Liste darf nichts aufgebaut werden) ·
   * `[]` = wirklich keine Freigabe-Schritte.
   */
  gates?: InstanceGateView[] | null;
  /**
   * #330 (UC-1): die GESPEICHERTE Wahl je Schritt. Die Fakten in `gates[]` tragen nur die
   * ZAHL der gewählten Personen; WELCHE es sind, steht hier — die Karte braucht beides
   * (Zahl für den Satz, Kennungen für den Personen-Picker).
   */
  entscheiderOverrides?: Record<string, EntscheiderWahl>;
  /**
   * #P002 (UC-3): die aufgelösten FAKTEN je AGENT-Schritt. Dieselben drei Zustände wie
   * `gates` — `undefined` = älterer Master (Karte erscheint nicht) · `null` = Definition
   * nicht lesbar (Karte erscheint und sagt das) · `[]` = wirklich kein Agent-Schritt.
   */
  stepModels?: InstanceStepModelView[] | null;
  /** #P002 (UC-3): die GESPEICHERTE Wahl je Schritt (roh) — `stepModels[].chosen` spiegelt sie. */
  stepModelOverrides?: Record<string, string>;
  /**
   * #P002 (UC-5): die Namen, die als LAUF-Modell in Frage kommen — vom Master bereits
   * gefiltert auf „erfüllt die Anforderungen aller Schritte, die auf dem Lauf-Modell laufen
   * würden". `undefined` = älterer Master · `null` = nicht ermittelbar; beides fällt auf die
   * ungefilterte Liste zurück (eine leere Auswahl wäre eine Aussage, die wir dann nicht haben).
   */
  runModelChoices?: ModelTargetView[] | null;
  status: "draft" | "ready" | "not_ready" | "paused";
  gaps: ProcessReadinessGapView[];
  createdAt: string;
  updatedAt: string;
}

/**
 * #330 (Wire aus #329 UC-4): die ENTSCHEIDER-FAKTEN eines Freigabe-Schritts — der Teil, aus
 * dem der Vorschau-Satz entsteht. Der Master liefert FAKTEN, den Satz formuliert jede Fläche
 * in ihrer Sprache (die Admin-Oberfläche deutsch, die User-App englisch); die EINE
 * Logik-Wahrheit bleibt `resolveEntscheider`.
 *
 * ⚠ Bewusst OHNE `quorum` und ohne Personen-IDs: die offene Freigabe (`pendingApproval`)
 * trägt genau diese vier Felder — wer eine Freigabe sieht, muss nicht die Besetzung der
 * Firma erfahren.
 */
export interface EntscheiderFakten {
  /**
   * Fach-Rolle des Schritts; `null` = kein Rollen-Gate. Dann entscheidet der Inhaber (wenn er
   * darf) bzw. die frei gewählten Personen — oder niemand (#331: Readiness meldet das).
   */
  rolle: string | null;
  /**
   * Ist der Inhaber Teil der Entscheider-Menge? ⚠ Ohne Rolle UND ohne getroffene
   * Personen-Wahl ist er GESETZT, nicht gewählt (#331 `resolveEntscheider`) — die Oberfläche
   * zeigt das Kästchen dort angehakt und gesperrt.
   */
  inhaberGewaehlt: boolean;
  /**
   * Wie viele Personen namentlich gewählt sind; `null` = keine Wahl getroffen (dann gilt die
   * Rolle). ⚠ Eine Wahl **ERSETZT** die Rolle, sie ergänzt sie nicht.
   */
  personenGewaehlt: number | null;
  /**
   * Grösse der aufgelösten Entscheider-Menge — die Zahl, gegen die das Quorum zählt.
   *
   * ⚠ Am Wire deckt sie sich bei getroffener Wahl mit `personenGewaehlt` (der Master prunt
   * ausgeschiedene Personen per Sweep). Auseinanderfallen können die beiden nur in der
   * LOKALEN Vorschau, wo gegen das eben gelesene Adressbuch geschnitten wird.
   */
  anzahl: number;
}

/**
 * #330 (UC-1): ein Freigabe-Schritt, wie ihn die Einrichtungs-Sicht ausweist — die Fakten
 * PLUS Quorum und die zwei Erlaubnis-Schalter der Vorlage. Nur was hier `true` ist, darf die
 * Einrichtung überhaupt wählen (der Master wächtert es zusätzlich mit einem generischen 400).
 *
 * Flach wie am Wire (`GateEntscheiderView`) — dadurch IST ein Schritt zugleich sein
 * Fakten-Satz und geht ohne Umbau in die Schablone.
 */
/**
 * #P002 (UC-3, Wire aus dem Master): ein AGENT-Schritt in der Einrichtungs-Sicht — was die
 * Vorlage sagt, was gewählt ist, was daraus folgt, und was man stattdessen wählen könnte.
 *
 * ⚠ `effective`/`effectiveSource`/`erfuellend` sind `null` = **nicht ermittelbar** (kein
 * Agent gebunden, Modell-Auskunft nicht abrufbar) — NICHT „nichts". Die Zeile entsteht
 * trotzdem, sonst liesse sich eine bestehende Wahl weder sehen noch entfernen.
 */
export interface InstanceStepModelView {
  stepId: string;
  stepName: string;
  /** Was ein Modell können MUSS (Prozess ∪ Schritt); `{}` = keine Anforderung. */
  requirements: { sensitiveDataOk?: boolean; hosting?: "local" };
  /** Der Name aus der Vorlage; `null` = sie nennt keinen. */
  templateModel: string | null;
  /** `true` = VORSCHLAG (überschreibbar), `false` = VORGABE (bei gesetztem Namen gesperrt). */
  templateOverridable: boolean;
  /**
   * #P002 (UC-3, Fix-Runde 1): der PROZESS-Vorschlag — eine ANDERE Aussage als `templateModel`:
   * er gilt „für alle Schritte" und ist damit das Lauf-Modell, nicht die Aussage dieses
   * Schritts. Eigenes Feld, damit sich beide nicht überlagern.
   *
   * ⚠ OPTIONAL getippt (Review 7, NIT-4): ein Master vor dieser Fix-Runde liefert das Feld
   * nicht. Gelesen wird es deshalb überall mit `?? null` — sonst stünde „undefined" im Satz.
   */
  processSuggestion?: string | null;
  chosen: string | null;
  effective: string | null;
  effectiveSource: "template-fixed" | "setup-step" | "template-step" | "run" | null;
  erfuellend: string[] | null;
}

export interface InstanceGateView extends EntscheiderFakten {
  stepId: string;
  /** Klarname aus der aktiven Definition (fällt serverseitig auf die stepId zurück). */
  stepName: string;
  /** Wie viele Zustimmungen der Schritt braucht. */
  quorum: number;
  inhaberErlaubt: boolean;
  personenWahlErlaubt: boolean;
  /**
   * Die aufgelöste Id der Fach-Rolle — sie MARKIERT die Rollen-Mitglieder im Adressbuch
   * (`users/available?roleId=`) und speist den Skew-Rückfall auf `members/available`.
   *
   * ⚠ `null` bei gesetzter `rolle` heisst „nicht auflösbar" (die Rolle existiert nicht mehr
   * oder heisst anders). Die Auswahl bleibt dann trotzdem möglich (seit #331 braucht die
   * freie Wahl keine Rolle) — es wird nur nichts markiert. Die Oberfläche darf die Id NICHT
   * aus dem Namen erraten: die Namens-Normalisierung ist Sache des Masters, und ein
   * Fehlgriff markierte die Mitglieder einer FREMDEN Rolle.
   */
  rolleId: string | null;
}

/**
 * #330 (UC-1) / #329 (UC-2): die Wahl der Einrichtung an EINEM Schritt.
 *
 * ⚠ Drei Zustände von `personen`: `undefined` = nichts gewählt · `null` = ausdrücklich ALLE
 * Mitglieder · `string[]` = genau diese. Ein `[]` ist etwas anderes als `null` — es heisst
 * „niemand" und wird deshalb vor dem Speichern beanstandet.
 */
export interface EntscheiderWahl {
  inhaber?: boolean;
  personen?: string[] | null;
}

/**
 * #330 (Wire aus #329 UC-4): ein wählbares Rollen-Mitglied (`GET /roles/:id/members/available`).
 * `anzeigeName` ist PII-minimal — der Master nimmt die E-Mail nur, wenn kein Name existiert.
 */
export interface RoleMemberAvailable {
  userId: string;
  anzeigeName: string;
}

// ⚠ #331: `RoleMemberAvailable` ist seit der freien Personen-Wahl nur noch der Skew-Rückfall
// der Entscheider-Karte — die reguläre Quelle ist `AvailableUser` (das Firmen-Adressbuch).

// Konfigurations-Sicht der aktiven Template-Version (GET /process-instances/:id/template-spec).
export interface TemplateSpecView {
  templateId: string;
  version: string;
  triggerKinds: string[];
  input: { required: string[]; optional: string[] };
  casefields: Array<{ key: string; label: string; type: string; required: boolean; choices?: string[] }>;
  settings: Record<string, { value: string | number | boolean; overridable: boolean }>;
  rollen: string[];
  fixedAgentId: string | null;
  systems: Array<{ connectorId: string; access: string }>;
}

export interface InstantiatorGrantView {
  userId: string;
  grantedBy: string;
  grantedAt: string;
}

// --- #301 (UC-5b): Läufe (Cases) ----------------------------------------------------------
// Die Executions-Sicht. Spiegelt `CaseView`/`CaseEventView` des Masters — die UI rechnet
// nichts nach, sie zeigt, was der Master als Wahrheit liefert.

export type CaseStatusValue =
  | "running"
  | "waiting_input"
  | "waiting_approval"
  | "blocked"
  | "paused"
  | "done"
  | "aborted";

export interface CaseView {
  id: string;
  instanceId: string;
  templateId: string;
  templateVersion: string;
  /**
   * ⚠ #302 (UC-5): `agentId`, `agentName`, `sessionId` und `casefields` FEHLEN, wenn der
   * Abrufende den Lauf nur als BETEILIGTER sieht (Gate-/Rückfrage-/Eskalations-Rolle statt
   * Inhaber). Weggelassen, nicht `null` — die UI muss also auf „Key fehlt" prüfen und darf
   * die Werte nicht blind lesen.
   */
  agentId?: string;
  agentName?: string | null;
  /** Beim Start eingefroren; `null` nur bei Läufen aus der Zeit vor Migration 048. */
  ownerId: string | null;
  /** #302 (UC-5): Klarname des Inhabers (= Login-Email); `null` = nicht nachschlagbar. */
  ownerEmail: string | null;
  sessionId?: string;
  triggerKind: string;
  startedBy: string | null;
  dryRun: boolean;
  status: CaseStatusValue;
  steps: Array<{ id: string; status: string }>;
  currentStepId: string | null;
  attempt: number;
  expiresAt: string | null;
  casefields?: Record<string, unknown>;
  /**
   * #P001 (UC-4): womit der Lauf arbeitet (`Session.model` der Case-Session). Getippt an
   * `CaseView`, weil Liste UND Detail es tragen. `undefined` = nicht geliefert
   * (Beteiligten-Sicht, Schreib-Antworten oder älterer Master), `null` = nicht
   * nachschlagbar — die Oberfläche zeigt beides als „—".
   *
   * ⚠ Der AKTUELLE Stand, nicht die Start-Wahl: die steht unveränderlich im `start`-Ereignis
   * des Verlaufs.
   */
  model?: string | null;
  createdAt: string;
  updatedAt: string;
  finishedAt: string | null;
}

export interface CaseListResult {
  cases: CaseListEntry[];
  total: number;
}

/**
 * #301 (UC-5d): die offene Rückfrage eines Laufs. Der Master leitet sie aus dem Verlauf ab
 * — sie ist Teil des LAUFS, keine Nachricht.
 */
export interface PendingAsk {
  /** Leer = Frage nicht abrufbar. Das Antwortfeld erscheint trotzdem — es ist der einzige
   *  Weg, den wartenden Lauf wieder in Gang zu bringen. */
  frage: string;
  stepId: string;
  stepName: string;
  /** #302 (UC-2): WER ist gefragt? `null` = der Lauf-Inhaber selbst. */
  askRole: string | null;
  /** `null` = Zeitpunkt unbekannt (kein lesbares ask-Ereignis). */
  gestelltAm: string | null;
}

/**
 * Das Lauf-DETAIL trägt die Rückfrage mit. Eigener Typ statt eines optionalen Feldes an
 * `CaseView`: dort hieße `undefined` „nicht ermittelt" und `null` „keine offene Rückfrage",
 * und die Oberfläche liest so eine Unterscheidung verlässlich falsch.
 */
export interface CaseDetail extends CaseView {
  pendingAsk: PendingAsk | null;
  /**
   * #303 (Review M2): die Kosten des Laufs — dieselbe Quelle wie in der Liste.
   * `null` = nicht nachschlagbar, `undefined` = nicht geliefert (Beteiligten-Sicht oder
   * älterer Master). Beides zeigt die Oberfläche als „—", nie als „$0.00".
   */
  costUsd?: number | null;
  /**
   * #P002 (UC-5): worauf dieser Lauf umgestellt werden DARF — vom Master gefiltert (siehe
   * `ProcessInstanceView.runModelChoices`). `undefined` = älterer Master ODER Beteiligten-Sicht
   * (dort ist es wie `model`/`costUsd` weggelassen) · `null` = nicht ermittelbar; beides fällt
   * auf die ungefilterte Liste zurück.
   */
  modelChoices?: ModelTargetView[] | null;
  /**
   * #302 (UC-1): die offene Freigabe — gesetzt genau dann, wenn
   * `status === "waiting_approval"`. Version-Skew-optional getippt: ein Master ohne #302
   * liefert das Feld nicht, und „fehlt" darf hier dieselbe (gutartige) Wirkung haben wie
   * „keine offene Freigabe" — die Maske erscheint dann schlicht nicht.
   */
  pendingApproval?: PendingApproval | null;
  /**
   * #328 (UC-4): wann wird der Lauf gelöscht? Berechnet aus `finishedAt` + der
   * Aufbewahrungsfrist des Prozesses. `null` = **wird nicht gelöscht** (noch nicht beendet,
   * kein Regler deklariert, oder die Frist ist gerade nicht auflösbar), `undefined` =
   * älterer Master. Beides zeigt die Oberfläche als „—".
   */
  loeschDatum?: string | null;
}

export interface CaseEventView {
  id: number | null;
  ts: string;
  type: string;
  actor: string;
  stepId: string | null;
  attempt: number | null;
  data: Record<string, unknown>;
}

/**
 * #302 (UC-1): die offene Freigabe eines Laufs — genau dann gesetzt, wenn
 * `status === "waiting_approval"`.
 *
 * ⚠ `zustimmungen: null` heisst **nicht ermittelbar**, NICHT „0" (Master-Kommentar): eine
 * stille 0 läse sich als „niemand hat entschieden" und löste beim Entscheider genau die
 * Handlung aus, die er vielleicht schon getan hat.
 */
export interface PendingApproval {
  stepId: string;
  stepName: string;
  approverRole: string;
  quorum: number;
  zustimmungen: number | null;
  entschiedenVon: string[] | null;
  /** Felder, die dieser Schritt liefern soll (das Formular) — leer = reine Freigabe. */
  produces: string[];
  /** Der eingefrorene Feldstand, über den entschieden wird. `null` = nicht lesbar/zu gross. */
  snapshot: Record<string, unknown> | null;
  /** Feldnamen eines GEKÜRZTEN Snapshots; `null` = nicht gekürzt. */
  snapshotKeys: string[] | null;
  /**
   * #303 (Review H1): die Feld-Beschreibungen zu `produces` — Typ, Beschriftung, Auswahl.
   *
   * ⚠ Sie kommen MIT der Freigabe und nicht aus der Konfigurations-Sicht der Einrichtung:
   * der Entscheider ist per Definition nicht deren Inhaber und bekäme dort ein 404. Ohne
   * sie wäre jedes Feld ein Textfeld — ein `money`/`choice`-Gate liesse sich gar nicht
   * abschliessen. Version-Skew-optional getippt: ein älterer Master liefert nichts, dann
   * gilt der markierte Rohfeld-Rückfall wie bisher.
   */
  feldDefs?: Array<{ key: string; label: string; type: string; required: boolean; choices?: string[] }>;
  angefragtAm: string | null;
  /**
   * #323/#324: die WIEVIELTE Vorlage liegt hier zur Entscheidung? 1 = die erste, jede
   * Überarbeitung zählt eine hoch.
   *
   * ⚠ `null` = **nicht ermittelbar** (Ereignis-Log nicht lesbar) — dieselbe Regel wie bei
   * `zustimmungen`: eine stille „1" behauptete „erste Vorlage" und verschwiege dem
   * Entscheider genau das, was er wissen muss. `undefined` = älterer Master ohne #323.
   * Beides führt zur selben (zurückhaltenden) Anzeige: kein Runden-Hinweis.
   */
  runde?: number | null;
  /**
   * #323/#324: darf dieser Entscheider „zur Überarbeitung zurück" wählen — und wie oft noch?
   *
   * `null` ODER fehlend ⇒ der dritte Knopf erscheint gar nicht erst. Der Master lehnt ein
   * `revise` an einem Gate ohne `onReject:revise` mit `REVISE_NOT_DECLARED` ab; ein Knopf,
   * der in einen Fehler läuft, ist schlechter als keiner (default-closed).
   */
  ueberarbeitung?: PendingApprovalRevise | null;
  /**
   * #330 (UC-2, Wire aus #329 UC-4): WER gerade gefragt ist — dieselben Fakten wie an der
   * Einrichtung, additiv angehängt.
   *
   * ⚠ `undefined`/`null` = älterer Master bzw. nicht ermittelbar. Dann bleibt die Karte
   * byte-gleich bei der heutigen Rollen-Anzeige; eine erfundene Menge wäre die schlechteste
   * aller Auskünfte, weil der Entscheider daraus schliesst, ob er allein zuständig ist.
   */
  entscheider?: EntscheiderFakten | null;
}

/** #323/#324: die Überarbeitungs-Option eines Gates, wie der Entscheider sie sieht. */
export interface PendingApprovalRevise {
  /** Schritt, an den der Lauf zurückgeht. */
  zielStepId: string;
  /** Klarname des Ziel-Schritts (aus der gepinnten Vorlage) — das, was der Mensch liest. */
  zielStepName: string;
  /** `null` = unbegrenzt viele Überarbeitungen (Default-Politik). */
  maxRounds: number | null;
}

// --- #303 (UC-1): Listen-Form + Step-Faltung + Dateien + Protokoll ------------------------

/**
 * Die LISTEN-Form eines Laufs. Eigener Typ am Master, weil die Liste `currentStep`/`costUsd`
 * IMMER füllen kann und die Schreib-Verben nie — ein optionales Feld hiesse in der einen Form
 * „nicht da" und in der anderen „gibt es nicht".
 *
 * ⚠ Beide Felder sind Version-Skew-optional getippt: ein älterer Master liefert `GET /cases`
 * ohne sie, und daraus darf keine Falschaussage („keine Kosten") werden.
 */
export interface CaseListEntry extends CaseView {
  currentStep?: { id: string; name: string | null } | null;
  costUsd?: number | null;
}

export type StepFoldStatus = "pending" | "running" | "waiting" | "blocked" | "done" | "skipped";

export interface StepFrage {
  frage: string;
  askRole: string | null;
  optionen: string[];
  gestelltAm: string;
  antwort: string | null;
  antwortVon: string | null;
  beantwortetAm: string | null;
}

export interface StepEntscheidung {
  /** `approve` | `reject` | `revise` (roh aus dem Ereignis — ältere Läufe tragen anderes). */
  entscheid: string;
  von: string;
  am: string;
  final: boolean | null;
  quorum: number | null;
  zustimmungen: number | null;
  approverRole: string | null;
  /**
   * #323/#324: die Runde, in der entschieden wurde (1-basiert). `null`/fehlend = im Ereignis
   * nicht vermerkt (Läufe von vor #323) ⇒ das Label trägt KEINEN Zusatz. Niemals „Runde
   * undefined" — eine erfundene Runde wäre eine Aussage über etwas, das der Lauf nicht weiss.
   */
  runde?: number | null;
  /**
   * #323/#324: der Überarbeitungs-Auftrag bzw. die Begründung — der Text, der neben „Runde N"
   * steht. `null`/fehlend = keiner mitgegeben.
   */
  feedback?: string | null;
}

export interface StepToolPermission {
  requestId: string | null;
  werkzeugText: string;
  anzeige: string | null;
  empfaengerRolle: string | null;
  fallbackOwner: boolean;
  angefragtAm: string | null;
  /** `allow` | `deny` | `timeout` | `undeliverable`; `null` = noch offen. */
  entscheid: string | null;
  von: string | null;
  entschiedenAm: string | null;
}

export interface StepEskalation {
  grund: string;
  escalationRole: string | null;
  am: string;
}

export interface StepRedispatch {
  /** `retry` | `redispatch`. */
  ausloeser: string;
  von: string;
  am: string;
}

export interface StepHinweis {
  art: "blocked" | "skipped";
  grund: string | null;
  am: string;
}

export interface CaseStepFile {
  attachmentId: string;
  filename: string;
  mimetype: string;
  size: number;
  version: number;
  latest: boolean;
  deliveredAt: string;
}

export interface CaseStepFold {
  id: string;
  nummer: number;
  /** Klarname aus der gepinnten Vorlage; `null` = Vorlage nicht lesbar (Beiwerk). */
  name: string | null;
  /** `agent` | `mensch` | `grenzagent`; `null` = Vorlage nicht lesbar. */
  akteur: string | null;
  status: StepFoldStatus;
  begonnenAm: string | null;
  beendetAm: string | null;
  /** Anzahl Zustellungen an den Agenten. */
  zustellungen: number;
  input: Record<string, unknown> | null;
  inputUnvollstaendig: boolean;
  output: Record<string, unknown> | null;
  outputGekuerzt: boolean;
  outputKeys: string[] | null;
  fragen: StepFrage[];
  entscheidungen: StepEntscheidung[];
  toolPermissions: StepToolPermission[];
  eskalationen: StepEskalation[];
  redispatches: StepRedispatch[];
  dateien: CaseStepFile[];
  hinweise: StepHinweis[];
}

export interface CaseStepsResult {
  caseId: string;
  /** War die gepinnte Vorlage lesbar? `false` ⇒ Namen/Akteur/Input fehlen überall. */
  specLesbar: boolean;
  /** Die Chronik war länger als der Faltungs-Deckel — gefaltet wurde ihr ANFANG. */
  gekuerzt: boolean;
  steps: CaseStepFold[];
}

/** Eine im Lauf abgegebene Datei (Listen-Form, mit Schritt-Bezug). */
export interface CaseFileEntry {
  attachmentId: string;
  stepId: string | null;
  filename: string;
  mimetype: string;
  size: number;
  deliveredAt: string;
  version: number;
  latest: boolean;
}

/** Eine rohe Modell-Nachricht. `message` ist für uns OPAK — der Renderer deutet sie. */
export interface SessionMessageEntry {
  type: "user" | "assistant" | "system";
  uuid: string;
  sessionId: string;
  message: unknown;
}

export interface CaseStepTranscriptResult {
  caseId: string;
  stepId: string;
  available: boolean;
  grund: "step-laeuft" | "noch-nicht-gelaufen" | "kein-transcript" | "nicht-verfuegbar" | null;
  gekuerzt: boolean;
  messages: SessionMessageEntry[];
}

/**
 * #303 (UC-1): Lauf-Kennzahlen einer Vorlage.
 *
 * ⚠ Nenner-Falle: `laeufe` ist die exakte Gesamtzahl, aber `proStatus`/`fehlerquote`
 * beschreiben nur die `ausgewertet`-Stichprobe. `gekuerzt` ist das Signal, dass die
 * Oberfläche das benennen MUSS.
 */
export interface ProcessStatsView {
  processId: string;
  laeufe: number;
  ausgewertet: number;
  gekuerzt: boolean;
  proStatus: Record<string, number>;
  durchlaufzeitSek: { avg: number; median: number; n: number } | null;
  /** Anteil 0..1 an den AUSGEWERTETEN Läufen. */
  fehlerquote: number;
  instanzen: number;
}
